import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { ExpandableComponent } from '../../../shared/expandable-component/expandable.component';
import { IconComponent } from '../../../shared/icons/icon.component';
import { IconNameType } from '../../../shared/icons/icon-name-type.enum';
import { CommonModule } from '@angular/common';
import { IngredientChoice } from '../../models/menu/ingredient-choice.model';
import { OrderedChoice } from '../../models/ordering/ordered-choice.model';
import { IngredientGroupComponent } from '../ingredient-group/ingredient-group.component';
import { OrderedChoiceService } from '../../services/ordering/ordered-choice.service';
import { OrderedItem } from '../../models/ordering/ordered-item.model';
import { ChoiceValidationResult } from '../../services/ordering/choice-validation-result.model';
import { OrderableItem } from '../../models/menu/orderable-item.model';
import { OrderableIngredient } from '../../models/menu/orderable-ingredient.model';
import { TranslocoPipe } from '@ngneat/transloco';
import { DependableIngredientComponent } from "../dependable-ingredient/dependable-ingredient.component";
import { DependableIngredientMappingService } from '../../services/dependable-ingr.service';
@Component({
  selector: 'cwo-dependable-choice',
  standalone: true,
  imports: [
    ExpandableComponent,
    IconComponent,
    CommonModule,
    IconComponent,
    IngredientGroupComponent,
    TranslocoPipe,
    DependableIngredientComponent
],
  templateUrl: './dependable-choice.component.html',
  styleUrl: './dependable-choice.component.scss',
})
export class DependableChoiceComponent implements OnChanges, AfterViewInit {
  orderedChoiceService = inject(OrderedChoiceService);
  private dependableIngredientMappingService = inject(DependableIngredientMappingService);

  choice = input.required<IngredientChoice>();
  iconNameType = IconNameType;
  orderedChoice = input.required<OrderedChoice>();

  orderedItem = input.required<OrderedItem>();
  orderableItem = input.required<OrderableItem>();
  
  firstInvalidChoice = input('');
  isSpecial = input.required<boolean>();

  toggleWarningsAfterSubmission = input(false);
  bouncingTextToggle = false;

  choiceExpandableId = computed(
    () => `${this.choice().name}-expandable-section`
  );

  filteredIngredientsByGroup = computed(() => {
    const groups = this.choice().groups;
    const ingredients = this.choice().ingredients;
  
    return groups.map(group => ({
      ...group,
      filteredIngredients: ingredients.filter(ingredient => 
        group.ingredients.includes(ingredient.name)
      )
    }));
  });

  nestedChoices = computed(() =>
    this.dependableIngredientMappingService.getSelectedDescendantChoices(
      (this.orderedChoice()?.ingredients ?? []).map(i => i.ingredient),
      this.orderedItem()
    )
  );

  groupsWithMissingNestedRequiredSelection = computed(() =>
    this.dependableIngredientMappingService.getGroupsWithMissingRequiredSelection(
      this.choice().groups,
      this.orderedChoice(),
      this.orderedItem()
    )
  );

  missingNestedRequiredSelection = computed(() =>
    this.dependableIngredientMappingService.findMissingRequiredSelection(this.nestedChoices(), this.orderedItem())
  );

  warningPillType?: 'neutural' | 'error' | 'complete';
  choiceValidator?: ChoiceValidationResult;

  expanded = false;
  scrollMarginTop?: number;
  scrollMarginTopWarning?: number;

  readonly warningMessageRef = viewChild<ElementRef>('warningMessageElement');
  ingredientTooltipVisible: string = '';

  expandedInput = input<boolean>(false);

  readonly resetWarningsAfterSubmissionValue = output();

  readonly updateSpecialItem = output<OrderedItem>();

  private elRef = inject(ElementRef);

  constructor() {}

  calcualteScrollMarginTop() {
    const informationContainerElRef = document.getElementById(
      'item-editor-item-information-container'
    );
    this.scrollMarginTop = informationContainerElRef?.getBoundingClientRect()
      .height
      ? informationContainerElRef?.getBoundingClientRect().height + 10
      : undefined;

    this.scrollMarginTopWarning = this.scrollMarginTop
      ? this.scrollMarginTop + 22
      : undefined; //22px of the header
  }

  onIngredientAdded(ingredient: OrderableIngredient) {
    const orderedIngredient = this.orderedItem().getOrderedIngredient(this.choice(), ingredient);
    if(ingredient.isExtraIngredientUpsell && !this.orderableItem().isExtraIngredientTooltipOpenedOnce && !!ingredient.qualifiers.find(q => q.name === 'Extra') && !orderedIngredient?.qualifiers.includes('Extra')) {
      this.ingredientTooltipVisible = ingredient.name;
    }
  }

  ngAfterViewInit(): void {
    this.checkIfChoiceIsValid(true);
    
    if(this.expandedInput()){
      this.expanded = this.expandedInput();
    }
  }

  checkIfChoiceIsValid(controlExpansion = false) {
    this.choiceValidator = this.orderedChoiceService.validateChoice(
      this.orderedChoice(),
      this.choice()
    );
    if (this.choiceValidator) {
      this.orderedChoice().warnings = this.choiceValidator?.valid
        ? []
        : [this.choiceValidator?.longMessage];

    if(controlExpansion){
      this.expanded =
        this.choice().isOpenOnWeb ||
        (this.choiceValidator && !this.choiceValidator.valid);
    }
  }
}
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['toggleWarningsAfterSubmission'] ||
      changes['firstInvalidChoice']
    ) {
      if (
        this.toggleWarningsAfterSubmission() &&
        this.firstInvalidChoice() === this.choice().name
      ) {
        this.calcualteScrollMarginTop();
        this.elRef.nativeElement.setAttribute(
          'style',
          `scroll-margin-top: ${this.scrollMarginTopWarning}px`
        );
        this.elRef?.nativeElement.scrollIntoView({ behavior: 'smooth' });
      } else if (
        !changes['firstInvalidChoice']?.firstChange &&
        this.firstInvalidChoice() &&
        this.nestedChoices().some((nestedChoice) => nestedChoice.name === this.firstInvalidChoice())
      ) {
        this.expanded = true;
      }
      
    }
    if(changes['orderedChoice'] && this.choice().ingredients.length > 0) {
      this.checkIfChoiceIsValid();
    }
  }

  handleExpansion(expanded: boolean) {
    this.calcualteScrollMarginTop();
    this.expanded = expanded;
    if (this.expanded) {
      const modalContent = document.getElementById(
        'item-editor-scrollable-wrapper'
      );
      const el = document.getElementById(this.choiceExpandableId());
      const elContent = document.getElementById(
        this.choiceExpandableId() + 'content'
      );
      const footer = document.getElementById('item-editor-footer');

      if (el && elContent && modalContent && footer) {
        const modalContentRect = modalContent.getBoundingClientRect();
        const elContentRect = elContent.getBoundingClientRect();
        const footerRect = footer.getBoundingClientRect();

        // Calculate the visible area within the modal content
        const visibleAreaHeight = modalContentRect.height - footerRect.height;

        // Check if the content's bottom position exceeds the visible area
        if (elContentRect.bottom > visibleAreaHeight) {
          // Wait for the category to be fully expanded before scrolling
          setTimeout(() => {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 10);
        }
      }
    }
  }

  setWarningMessage(choiceValidation: ChoiceValidationResult) {
    this.resetWarningsAfterSubmissionValue.emit();
    if (!choiceValidation.valid) {
      if(!this.choiceValidator?.valid){
        return;
      }
      this.orderedChoice().warnings = [choiceValidation.longMessage];
    } else {
      this.orderedChoice().warnings = [];
    }
    const invalidForFirstTime = choiceValidation.longMessage !== this.choiceValidator?.longMessage;
    this.choiceValidator = choiceValidation;

    if(invalidForFirstTime && choiceValidation.longMessage) {
      this.warningMessageRef()?.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
      this.bouncingTextToggle = true;
      //reseting bouncingtexttoggle after animation is done
      setTimeout(() => {
        this.bouncingTextToggle = false;
      }, 1500);
    }
  }
}
